import { http } from '@/lib/http';
import type { WhyScope, WhyView } from './types';

// The two operator-bff `why@v1` reads (feature disruption-why §8, slice W5).
// `http` is the single configured ky instance (Auth0 token + traceparent + 401
// handling) per frontend-conventions §6.4 — never a bare fetch.
//
// NOTE the missing leading slash: ky's `prefixUrl` requires relative paths.
//
// Both routes DEGRADE OPEN at the BFF: an unset or unreachable journal answers
// 200 with `narrative.known:false`, never a 404. So a rejected promise here
// means the READ itself failed (network, 401, 5xx) — which is the dialog's
// error state, and is distinct from "the account is empty".

/** `operator-bff:GET /v1/disruptions/{urn}/why@v1`. */
export async function fetchDisruptionWhy(disruptionUrn: string): Promise<WhyView> {
  return http.get(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/why`).json<WhyView>();
}

/**
 * `operator-bff:GET /v1/cases/{urn}/why@v1`.
 *
 * The evidence half is disruption-grained, so a caller that already knows the
 * disruption passes it and saves the BFF a lookup. Not knowing it is never an
 * error — the BFF resolves it from the chapters, and failing that returns the
 * account with `evidence.known:false`.
 */
export async function fetchCaseWhy(caseUrn: string, disruptionUrn?: string): Promise<WhyView> {
  const path = `v1/cases/${encodeURIComponent(caseUrn)}/why`;
  return http
    .get(path, disruptionUrn ? { searchParams: { disruptionUrn } } : undefined)
    .json<WhyView>();
}

export async function fetchWhy(scope: WhyScope): Promise<WhyView> {
  return scope.kind === 'disruption'
    ? fetchDisruptionWhy(scope.disruptionUrn)
    : fetchCaseWhy(scope.caseUrn, scope.disruptionUrn);
}
